// PlanCard.tsx
// Selectable plan card for tier + billing period selection

import React from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import { Text } from '../../../components/ui';
import { Theme } from '../../../utils/theme';
import { SubscriptionTierChoice, BillingPeriodChoice } from '../types';

interface PlanCardProps {
  tier: SubscriptionTierChoice;
  period: BillingPeriodChoice;
  priceString: string;
  isSelected: boolean;
  isRecommended?: boolean;
  onSelect: () => void;
  hasTrial?: boolean;
  savingsPercent?: number;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  tier,
  period,
  priceString,
  isSelected,
  isRecommended,
  onSelect,
  hasTrial,
  savingsPercent,
}) => {
  const tierName = tier === 'ai' ? 'AI Platinum' : 'Platinum';
  const periodName = period === 'annual' ? 'Annual' : 'Monthly';

  return (
    <Pressable
      onPress={onSelect}
      style={[
        styles.container,
        isSelected && styles.selected,
      ]}
    >
      {/* Header row with tier name and badges */}
      <View style={styles.header}>
        <Text variant="h3" style={styles.tierName}>{tierName}</Text>
        
        <View style={styles.badgesRow}>
          {savingsPercent && savingsPercent > 0 && (
            <View style={styles.savingsBadge}>
              <Text variant="caption" style={styles.savingsText}>Save {savingsPercent}%</Text>
            </View>
          )}
          {isRecommended && (
            <View style={[styles.badge, savingsPercent ? { marginLeft: Theme.spacing.xs } : {}]}>
              <Text variant="caption" style={styles.badgeText}>RECOMMENDED</Text>
            </View>
          )}
        </View>
      </View>

      <View style={styles.content}>
        <Text variant="body2" color="secondary" style={styles.period}>{periodName}</Text>
        
        <Text variant="h3" style={styles.price}>
          {priceString}
          <Text variant="body2" color="secondary"> /{period === 'annual' ? 'yr' : 'mo'}</Text>
        </Text>

        {hasTrial && (
          <Text variant="caption" style={styles.trialText}>Includes free trial</Text>
        )}
      </View>

      {/* Selection indicator */}
      <View style={[styles.radio, isSelected && styles.radioSelected]}>
        {isSelected && <View style={styles.radioDot} />}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.lg,
    borderWidth: 2,
    borderColor: Theme.colors.border,
    marginBottom: Theme.spacing.md,
    position: 'relative',
  },
  selected: {
    borderColor: Theme.colors.primary,
    backgroundColor: Theme.colors.primaryLight,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: Theme.spacing.sm,
    marginRight: 40, // Space for radio button
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
  },
  badge: {
    backgroundColor: Theme.colors.primary,
    paddingHorizontal: Theme.spacing.sm,
    paddingVertical: Theme.spacing.xs,
    borderRadius: Theme.borderRadius.sm,
  },
  badgeText: {
    color: Theme.colors.textInverse,
    fontWeight: '700',
    fontSize: 10,
  },
  savingsBadge: {
    backgroundColor: Theme.colors.accent,
    paddingHorizontal: Theme.spacing.sm,
    paddingVertical: Theme.spacing.xs,
    borderRadius: Theme.borderRadius.sm,
  },
  savingsText: {
    color: Theme.colors.textInverse,
    fontWeight: '700',
    fontSize: 10,
  },
  content: {
    marginRight: 40,
  },
  tierName: {
    flex: 1,
    marginRight: Theme.spacing.sm,
  },
  period: {
    marginBottom: Theme.spacing.sm,
  },
  price: {
    color: Theme.colors.primary,
    paddingTop: 6, // Extra space at top to prevent text clipping
    paddingBottom: 2, // Balance the spacing
    marginBottom: Theme.spacing.xs,
  },
  trialText: {
    color: Theme.colors.primary,
    fontWeight: '600',
  },
  radio: {
    position: 'absolute',
    right: Theme.spacing.lg,
    top: '50%',
    marginTop: -12,
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: Theme.colors.border,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioSelected: {
    borderColor: Theme.colors.primary,
  },
  radioDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Theme.colors.primary,
  },
});

